import { useCallback, useRef, useState } from "react";
import api from "./api/client";
import { INITIAL_PAYMENT_STATE } from "./constants/payment";
import type {
  LogMessage,
  PaymentEvent,
  PaymentHandlerMessages,
  PaymentInitHandlers,
  PaymentPatch,
  PaymentSource,
  PaymentState,
} from "./types/payment";
import { PaymentFlowStatus, PaymentResultStatus } from "./types/payment";
import { createLogMessage, createPaymentLogEntry, PROCESSING_INVOICE_KEY } from "./utils/payment";

const sameLogEntry = (a: LogMessage, b: LogMessage): boolean =>
  a.key === b.key && JSON.stringify(a.params ?? null) === JSON.stringify(b.params ?? null);

const currentGroupStartIndex = (entries: LogMessage[]): number => {
  const reversed = [...entries].reverse();
  const found = reversed.findIndex((entry) => entry.key === PROCESSING_INVOICE_KEY);
  return found === -1 ? 0 : entries.length - 1 - found;
};

const isDuplicateInCurrentGroup = (entries: LogMessage[], newEntry: LogMessage): boolean =>
  entries.slice(currentGroupStartIndex(entries)).some((entry) => sameLogEntry(entry, newEntry));

const createLogPatch = (prev: PaymentState, newEntry: LogMessage): PaymentPatch => {
  if (isDuplicateInCurrentGroup(prev.logEntries, newEntry)) return { logEntries: prev.logEntries };
  return { logEntries: [...prev.logEntries, newEntry] };
};

const usePaymentBase = () => {
  const [state, setState] = useState<PaymentState>(INITIAL_PAYMENT_STATE);
  const processedInvoiceIds = useRef(new Set<number>());

  const onSocketDisconnectRef = useRef<() => void>(() => {});
  const onConnectToPaymentRef = useRef<(id: number) => void>(() => {});
  const onLogReloadRef = useRef<() => void>(() => {});
  const messagesRef = useRef<PaymentHandlerMessages>({
    noInvoices: "No invoices to pay",
    paymentFailed: "Payment failed",
  });

  const initPaymentHandlers = useCallback((handlers: PaymentInitHandlers) => {
    if (handlers.onSocketDisconnect) onSocketDisconnectRef.current = handlers.onSocketDisconnect;
    if (handlers.onConnectToPayment) onConnectToPaymentRef.current = handlers.onConnectToPayment;
    if (handlers.onLogReload) onLogReloadRef.current = handlers.onLogReload;
    if (handlers.messages) messagesRef.current = handlers.messages;
  }, []);

  const patch = useCallback((partial: PaymentPatch | ((prev: PaymentState) => PaymentPatch)) => {
    setState((prev) => {
      const p = typeof partial === "function" ? partial(prev) : partial;
      const resolved: Record<string, unknown> = {};

      for (const key in p) {
        const val = p[key as keyof PaymentPatch];
        const prevVal = prev[key as keyof PaymentState];
        resolved[key] = typeof val === "function" ? (val as (prev: unknown) => unknown)(prevVal) : val;
      }
      return { ...prev, ...resolved } as PaymentState;
    });
  }, []);

  const processPaymentEvent = useCallback(
    (event: PaymentEvent) => {
      switch (event.type) {
        case "progress":
          if (event.result.invoiceId != null) processedInvoiceIds.current.add(event.result.invoiceId);
          patch((prev) => ({
            processedCount: processedInvoiceIds.current.size,
            payResults: [...(prev.payResults ?? []), createPaymentLogEntry(event.result)],
          }));
          break;
        case "done": {
          const hasFailure = event.results?.some((r) => r.success === PaymentResultStatus.FAILURE);
          patch({
            payResult: event.message,
            paymentStatus: hasFailure ? PaymentFlowStatus.ERROR : PaymentFlowStatus.DONE,
            payResults: event.results?.map(createPaymentLogEntry) ?? null,
            twoFaPrompt: null,
          });
          processedInvoiceIds.current.clear();
          onSocketDisconnectRef.current();
          onLogReloadRef.current();
          break;
        }
        case "error":
          patch({ payError: event.message ?? event.key ?? "Payment failed", paymentStatus: PaymentFlowStatus.ERROR, twoFaPrompt: null });
          onLogReloadRef.current();
          break;
        case "dismiss_2fa":
          patch({ twoFaPrompt: null });
          break;
        case "log": {
          const newEntry = createLogMessage({ key: event.key, params: event.params });
          patch((prev) => createLogPatch(prev, newEntry));
          break;
        }
        case "prompt_2fa":
          patch({ twoFaPrompt: { requestId: event.requestId, message: event.message } });
          break;
      }
    },
    [patch],
  );

  const executePayment = useCallback(
    async (invoiceIds: number[], source?: PaymentSource) => {
      if (!invoiceIds?.length) {
        patch({ payError: messagesRef.current.noInvoices });
        return;
      }

      patch({
        paying: true,
        payResult: null,
        payError: null,
        payResults: null,
        paymentStatus: PaymentFlowStatus.PROCESSING,
        processedCount: 0,
        logEntries: [],
        twoFaPrompt: null,
        twoFaCode: "",
      });
      processedInvoiceIds.current.clear();
      onSocketDisconnectRef.current();

      try {
        const result = await api.actions.payInvoice(invoiceIds, source);
        patch({ currentPaymentLogId: result.paymentLogId });
        onConnectToPaymentRef.current(result.paymentLogId);
      } catch (err) {
        console.error("Failed to pay invoices:", err);
        patch({
          paymentStatus: PaymentFlowStatus.ERROR,
          payError: err instanceof Error ? err.message : messagesRef.current.paymentFailed,
        });
      }
    },
    [patch],
  );

  const cancelPayment = useCallback(
    async (message: string) => {
      onSocketDisconnectRef.current();

      if (state.currentPaymentLogId) await api.paymentLogs.cancel(state.currentPaymentLogId);

      patch({
        paying: false,
        paymentStatus: PaymentFlowStatus.ERROR,
        payError: message,
        currentPaymentLogId: null,
      });
    },
    [state.currentPaymentLogId, patch],
  );

  const submit2Fa = useCallback(
    async (code?: string) => {
      const twoFaCode = (code ?? state.twoFaCode).trim();

      if (!state.twoFaPrompt || !twoFaCode) return;

      try {
        await api.actions.submit2fa(twoFaCode);
        patch({ twoFaPrompt: null, twoFaCode: "" });
      } catch (err) {
        patch({ payError: err instanceof Error ? err.message : "2FA submission failed" });
      }
    },
    [state.twoFaPrompt, state.twoFaCode, patch],
  );

  const cancel2Fa = useCallback(() => {
    patch({ twoFaPrompt: null, twoFaCode: "" });
  }, [patch]);

  const loadPaymentLogs = useCallback(
    async (showLoading = true) => {
      if (showLoading) patch({ paymentLogsLoading: true });
      patch({ paymentLogsError: null });

      try {
        const { data } = await api.paymentLogs.list();
        patch({ paymentLogs: data });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        patch({ paymentLogsError: msg });
        console.error("Failed to load payment logs:", msg);
      } finally {
        if (showLoading) patch({ paymentLogsLoading: false });
      }
    },
    [patch],
  );

  const resetPayment = useCallback(() => {
    patch({
      paymentStatus: PaymentFlowStatus.IDLE,
      payResult: null,
      payError: null,
      payResults: null,
      processedCount: 0,
      logEntries: [],
      currentPaymentLogId: null,
      paying: false,
      twoFaPrompt: null,
      twoFaCode: "",
    });
  }, [patch]);

  return {
    state,
    patch,
    processedInvoiceIds,
    initPaymentHandlers,
    processPaymentEvent,
    executePayment,
    cancelPayment,
    submit2Fa,
    cancel2Fa,
    loadPaymentLogs,
    resetPayment,
  };
};

export { usePaymentBase };
